import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '@vaptmenu/analytics-react';
import {
  getCurrentSubscription,
  createSubscription,
  getInvoices,
  getPricing,
  cancelCurrentSubscription,
  getPlanChangeQuote,
  requestPlanChange,
  cancelPlanChange,
  getAiUnits,
  getAiUnitEntries,
  getAiUnitPurchase,
  createAiUnitPurchase,
} from '../api/billing.api';
import type {
  CurrentSubscription,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  InvoicesResponse,
  InvoicesQueryParams,
  PricingInfo,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  PlanChangeQuote,
  PlanChangeRequest,
  PlanChangeResult,
  CancelPlanChangeRequest,
  PlanCode,
  AiUnitsSummary,
  AiUnitLedgerQueryParams,
  AiUnitLedgerResponse,
  AiUnitPurchase,
  CreateAiUnitPurchaseRequest,
  CreateAiUnitPurchaseResult,
} from '../types/billing.types';
import { PLAN_CODES } from '../types/billing.types';
import { analyticsPlanOf } from '../lib/plans';
import type { ApiError } from '@/shared/lib/problem-details';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { billingKeys, revalidatePlanState } from './billing-keys';

export { billingKeys, revalidatePlanState, revalidateAiUnits } from './billing-keys';

/**
 * `{ plan }` para o `panel.subscription_action`, ou nada quando o código não
 * é um dos planos da taxonomia: o evento nunca leva um plano desconhecido.
 */
function planProperty(code: PlanCode | null | undefined) {
  const plan = analyticsPlanOf(code);
  return plan ? { plan } : {};
}

/* ==========================================================================
   Query Keys — scoped by tenant (billing-keys.ts)
   ========================================================================== */

function billingTenantId(): string {
  return useAuthStore.getState().selectedTenant?.id ?? '';
}

/**
 * Intervalo da reserva por consulta enquanto um upgrade aguarda pagamento:
 * cobre a conexão de tempo real caída, o membro sem `orders.read` e uma
 * imagem anterior da API, que não publica `billing.plan_changed`.
 */
export const AWAITING_PAYMENT_POLL_MS = 5_000;

/* ==========================================================================
   Queries
   ========================================================================== */

export interface UseCurrentSubscriptionOptions {
  enabled?: boolean;
  /**
   * Consulta a cada 5 s enquanto houver mudança de plano aguardando
   * pagamento, só com a aba visível, e para quando ela sai desse estado.
   * Só a página de assinatura liga: o painel inteiro não precisa.
   */
  pollWhileAwaitingPayment?: boolean;
}

export function isAwaitingPlanChangePayment(subscription: CurrentSubscription | undefined): boolean {
  return subscription?.pendingPlanChange?.status === 'awaiting_payment';
}

export function useCurrentSubscription(options?: UseCurrentSubscriptionOptions) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const poll = options?.pollWhileAwaitingPayment ?? false;
  return useQuery<CurrentSubscription, ApiError>({
    queryKey: billingKeys.subscription(tenantId),
    queryFn: getCurrentSubscription,
    enabled: !!tenantId && (options?.enabled ?? true),
    refetchInterval: poll
      ? (query) => (isAwaitingPlanChangePayment(query.state.data) ? AWAITING_PAYMENT_POLL_MS : false)
      : undefined,
    // Aba escondida não consulta: o foco de volta e o evento de tempo real cobrem.
    refetchIntervalInBackground: false,
  });
}

/**
 * Quando a mudança deixa de aguardar pagamento (pagou, venceu ou foi
 * apagada), revalida faturas e direitos, como o evento `billing.plan_changed`
 * faria. A assinatura é a própria leitura que mostrou a saída.
 */
export function useRevalidateWhenPlanChangeSettles(subscription: CurrentSubscription | undefined): void {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const status = subscription?.pendingPlanChange?.status;
  const previous = useRef(status);

  useEffect(() => {
    const was = previous.current;
    previous.current = status;
    if (was === 'awaiting_payment' && status !== 'awaiting_payment') {
      revalidatePlanState(queryClient, tenantId);
    }
  }, [status, tenantId, queryClient]);
}

export function usePricing() {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<PricingInfo, ApiError>({
    queryKey: billingKeys.pricing(tenantId),
    queryFn: getPricing,
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 30, // 30 minutes — prices rarely change
  });
}

export function useInvoices(params: InvoicesQueryParams = {}) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<InvoicesResponse, ApiError>({
    queryKey: billingKeys.invoices(tenantId, params as Record<string, unknown>),
    queryFn: () => getInvoices(params),
    enabled: !!tenantId,
  });
}

/**
 * Cotação da mudança para `toPlanCode`, sem gravar nada. Sempre relida ao
 * abrir: a diferença depende do dia, e uma cotação velha mostraria um valor
 * que o pedido não cobraria.
 */
export function usePlanChangeQuote(toPlanCode: PlanCode | null, options?: { enabled?: boolean }) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<PlanChangeQuote, ApiError>({
    queryKey: billingKeys.planChangeQuote(tenantId, toPlanCode ?? ''),
    queryFn: () => getPlanChangeQuote(toPlanCode as PlanCode),
    enabled: !!tenantId && !!toPlanCode && (options?.enabled ?? true),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/* ==========================================================================
   Mutations
   ========================================================================== */

export function useCreateSubscription() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<
    CreateSubscriptionResponse,
    ApiError,
    CreateSubscriptionRequest
  >({
    mutationFn: createSubscription,
    onSuccess: (_data, variables) => {
      // Sem `planCode` (um plano só à venda, ou API anterior) a API assina o Essencial.
      track('panel.subscription_action', {
        action: 'create',
        ...planProperty(variables.planCode ?? PLAN_CODES.essential),
        billing_cycle: variables.billingCycle,
      });
      const tid = billingTenantId();
      if (tid) {
        queryClient.invalidateQueries({
          queryKey: billingKeys.subscription(tid),
        });
      }
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<
    CancelSubscriptionResponse,
    ApiError,
    CancelSubscriptionRequest
  >({
    mutationFn: cancelCurrentSubscription,
    onSuccess: () => {
      track('panel.subscription_action', { action: 'cancel' });
      const tid = billingTenantId();
      if (!tid) return;
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription(tid) });
      queryClient.invalidateQueries({ queryKey: billingKeys.root(tid) });
    },
  });
}

/**
 * Recusas que dizem "o estado mudou por fora" (outra aba abriu uma mudança, o
 * pagamento chegou antes da desistência): o card relê a assinatura.
 */
function isStateConflict(error: ApiError): boolean {
  return error.status === 409;
}

/**
 * Pede a mudança de plano. Sucesso (inclusive o 202 do downgrade em
 * processamento, que a API aceitou) relê assinatura, faturas e direitos e
 * captura `change_plan` com o plano de destino; recusa não captura nada.
 * A repetição com a mesma `Idempotency-Key` de um pedido que o gateway
 * derrubou volta 200 com `status: "cancelled"`: o diálogo a mostra como
 * erro, e o evento também não sai, porque a mudança não foi aceita.
 */
export function useRequestPlanChange() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<PlanChangeResult, ApiError, PlanChangeRequest>({
    mutationFn: requestPlanChange,
    onSuccess: (result) => {
      if (result.status !== 'cancelled') {
        track('panel.subscription_action', {
          action: 'change_plan',
          ...planProperty(result.toPlanCode),
        });
      }
      revalidatePlanState(queryClient, billingTenantId());
    },
    onError: (error) => {
      const tid = billingTenantId();
      if (!tid) return;
      if (isStateConflict(error)) {
        void queryClient.invalidateQueries({ queryKey: billingKeys.subscription(tid) });
      }
      if (error.problem?.errorCode === 'billing.plan_not_found') {
        void queryClient.invalidateQueries({ queryKey: billingKeys.pricing(tid) });
      }
    },
  });
}

/**
 * Desiste da mudança aberta. Sucesso captura `cancel_plan_change` com o plano
 * de destino da mudança desistida, que a resposta traz; sucesso ou recusa por
 * estado relê a assinatura.
 */
export function useCancelPlanChange() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<PlanChangeResult, ApiError, CancelPlanChangeRequest>({
    mutationFn: cancelPlanChange,
    onSuccess: (result) => {
      track('panel.subscription_action', {
        action: 'cancel_plan_change',
        ...planProperty(result.toPlanCode),
      });
      revalidatePlanState(queryClient, billingTenantId());
    },
    onError: (error) => {
      const tid = billingTenantId();
      if (tid && isStateConflict(error)) {
        void queryClient.invalidateQueries({ queryKey: billingKeys.subscription(tid) });
      }
    },
  });
}

/* ==========================================================================
   Atendimentos do Atendente IA (change billing-ai-units)

   As leituras exigem `billing.read` e as conferem aqui, além da tela: a
   equipe sem a permissão nunca chama `/billing/ai-units` (a variante dela da
   faixa lê o status do atendente). A compra exige `billing.write`.
   ========================================================================== */

/** Validade da leitura do saldo: o tempo real e o foco da janela revalidam antes. */
export const AI_UNITS_STALE_TIME_MS = 30_000;

/** Releitura da compra aberta enquanto aguarda pagamento: a reserva do tempo real. */
export const AI_UNIT_PURCHASE_POLL_MS = 5_000;

function useHasPermission(permission: string): boolean {
  return useAuthStore((s) => s.user?.permissions.includes(permission) ?? false);
}

/** O saldo de agora (`GET /billing/ai-units`), só para quem tem `billing.read`. */
export function useAiUnits(options?: { enabled?: boolean }) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const canRead = useHasPermission('billing.read');
  return useQuery<AiUnitsSummary, ApiError>({
    queryKey: billingKeys.aiUnits(tenantId),
    queryFn: getAiUnits,
    enabled: !!tenantId && canRead && (options?.enabled ?? true),
    staleTime: AI_UNITS_STALE_TIME_MS,
  });
}

/** O extrato paginado (`GET /billing/ai-units/entries`), só com `billing.read`. */
export function useAiUnitEntries(params: AiUnitLedgerQueryParams = {}, options?: { enabled?: boolean }) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const canRead = useHasPermission('billing.read');
  return useQuery<AiUnitLedgerResponse, ApiError>({
    queryKey: billingKeys.aiUnitEntries(tenantId, params as Record<string, unknown>),
    queryFn: () => getAiUnitEntries(params),
    enabled: !!tenantId && canRead && (options?.enabled ?? true),
    staleTime: AI_UNITS_STALE_TIME_MS,
  });
}

export function isAiUnitPurchaseAwaitingPayment(purchase: AiUnitPurchase | undefined): boolean {
  return purchase?.status === 'pending';
}

/**
 * A compra aberta no diálogo, relida no `billing.ai_units_updated` (pela raiz
 * dos atendimentos) e, enquanto aguarda pagamento, a cada 5 s com a aba
 * visível. A resposta da compra já está no cache (`useCreateAiUnitPurchase`),
 * e a validade de 5 s evita lê-la de novo ao montar.
 */
export function useAiUnitPurchase(purchaseId: string | null, options?: { enabled?: boolean }) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const canRead = useHasPermission('billing.read');
  const id = purchaseId ?? '';
  return useQuery<AiUnitPurchase, ApiError>({
    queryKey: billingKeys.aiUnitPurchase(tenantId, id),
    queryFn: () => getAiUnitPurchase(id),
    enabled: !!tenantId && !!id && canRead && (options?.enabled ?? true),
    staleTime: AI_UNIT_PURCHASE_POLL_MS,
    refetchInterval: (query) =>
      isAiUnitPurchaseAwaitingPayment(query.state.data) ? AI_UNIT_PURCHASE_POLL_MS : false,
    refetchIntervalInBackground: false,
  });
}

/**
 * Compra um pacote. `panel.ai_units_purchase_created` sai só na resposta 201
 * (compra criada): a repetição pela mesma chave responde 200 e não captura,
 * para o clique duplo e a nova tentativa depois de um 503 não contarem duas
 * vezes; a recusa também não. O evento leva só o tamanho do pacote e a forma
 * de pagamento, nunca o copia-e-cola, o link, os dados de cobrança ou o id.
 */
export function useCreateAiUnitPurchase() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<CreateAiUnitPurchaseResult, ApiError, CreateAiUnitPurchaseRequest>({
    mutationFn: createAiUnitPurchase,
    onSuccess: (result) => {
      if (result.created) {
        track('panel.ai_units_purchase_created', {
          pack_units: result.purchase.units,
          payment_method: result.purchase.paymentMethod,
        });
      }
      const tid = billingTenantId();
      if (!tid) return;
      queryClient.setQueryData(billingKeys.aiUnitPurchase(tid, result.purchase.purchaseId), result.purchase);
      // A fatura `ai_units` pendente nasce com a compra.
      void queryClient.invalidateQueries({ queryKey: billingKeys.invoicesRoot(tid) });
    },
    onError: (error) => {
      const tid = billingTenantId();
      if (tid && error.problem?.errorCode === 'billing.ai_units_pack_not_found') {
        void queryClient.invalidateQueries({ queryKey: billingKeys.pricing(tid) });
      }
    },
  });
}
